import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
// ESM direct imports to guarantee functions in production
import AlertCircle from "lucide-react/dist/esm/icons/alert-circle";
import Download from "lucide-react/dist/esm/icons/download";
import Server from "lucide-react/dist/esm/icons/server";
import Copy from "lucide-react/dist/esm/icons/copy";
import { toast } from "sonner";

// Robust component resolver - handles ES modules, CommonJS, and various bundler behaviors
function resolveComponent<T>(component: T): any {
  const anyComp: any = component as any;

  // Handle various export patterns:
  // 1. { default: fn } - ES6 default export
  // 2. fn - direct function export
  // 3. { AlertCircle: fn } - named exports that become objects
  // 4. undefined - when imports fail

  if (anyComp?.default && typeof anyComp.default === "function") {
    return anyComp.default;
  }

  if (typeof anyComp === "function") {
    return anyComp;
  }

  // For named exports that might be objects with actual components nested
  for (const key of Object.keys(anyComp || {})) {
    if (typeof anyComp[key] === "function") {
      return anyComp[key];
    }
  }

  return null;
}

// Safe component renderer - bulletproof, never crashes React
const SafeComponentRenderer = (Component: any, props: any, fallback: React.ReactElement, debugLabel?: string) => {
  if (debugLabel && typeof window !== "undefined" && new URLSearchParams(window.location.search).has("debug")) {
    console.debug(`[SafeRenderer] ${debugLabel}: typeof=${typeof Component}, resolved=${resolveComponent(Component) ? 'function' : 'null'}`);
  }

  try {
    const ResolvedComponent = resolveComponent(Component);
    if (typeof ResolvedComponent === "function") {
      return <ResolvedComponent {...props} />;
    }
  } catch (error) {
    console.warn(`[SafeRenderer] Failed to render component:`, error);
  }

  return fallback;
};



interface SupportSnapshotPanelProps {
  snapshotData?: {
    app: {
      version: string;
      environment: string;
      platform: string;
      uptimeSec: number;
      node: string;
    };
    db: {
      ok: boolean;
    };
    storage: {
      ok: boolean;
    };
    features: {
      authProvider: string;
      offlineEnabled: boolean;
      storageConfigured: boolean;
    };
  };
  fullSnapshot?: any;
}

// Local error boundary for isolating sections within a panel
class SectionBoundary extends React.Component<{name: string, children: React.ReactNode}, {hasError: boolean, error: Error | null}> {
  constructor(props: {name: string, children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`[SupportSnapshot] SECTION CRASHED: ${this.props.name}`, error);
    if (typeof window !== "undefined" && new URLSearchParams(window.location.search).has("debug")) {
      console.error('[SectionBoundary] Error info:', errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-3 border border-destructive rounded-md bg-destructive/10">
          <h4 className="text-sm font-medium text-destructive">Section Crashed: {this.props.name}</h4>
          <p className="text-xs text-muted-foreground mt-1">
            Error: {this.state.error?.message || 'Unknown error'}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

// Split into subcomponents to isolate the crashing section
function SupportSnapshotHeader({ title, icon }: { title: string, icon: any }) {
  return (
    <div className="flex items-center gap-2">
      {SafeComponentRenderer(icon, { className: "h-5 w-5" }, <span aria-hidden="true" className="h-5 w-5">🖥️</span>, "HeaderIcon")}
      <CardTitle>{title}</CardTitle>
    </div>
  );
}

function SupportSnapshotActionsGrid({
  onCopySchematic,
  copyingSchematic,
  onCopyContext,
  copyingContext,
  onDownloadBundle,
  downloadingBundle,
  downloadIcon,
  serverIcon
}: {
  onCopySchematic: () => void;
  copyingSchematic: boolean;
  onCopyContext: () => void;
  copyingContext: boolean;
  onDownloadBundle: () => void;
  downloadingBundle: boolean;
  downloadIcon: any;
  serverIcon: any;
}) {
  return (
    <div className="space-y-3">
      {/* Copy actions group - secondary layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Button
          onClick={onCopySchematic}
          disabled={copyingSchematic}
          variant="outline"
          size="sm"
          className="whitespace-nowrap"
        >
          {SafeComponentRenderer(Copy, { className: "h-4 w-4 mr-2" }, <span aria-hidden="true" className="h-4 w-4 mr-2">📋</span>, "CopyIcon")}
          {copyingSchematic ? "Copying..." : "Copy App Schematic"}
        </Button>
        <Button
          onClick={onCopyContext}
          disabled={copyingContext}
          variant="outline"
          size="sm"
          className="whitespace-nowrap"
        >
          {SafeComponentRenderer(Copy, { className: "h-4 w-4 mr-2" }, <span aria-hidden="true" className="h-4 w-4 mr-2">📋</span>, "CopyIcon")}
          {copyingContext ? "Copying..." : "Copy Troubleshooting Context"}
        </Button>
      </div>

      {/* Download action - primary/emphasized/prominent */}
      <Button
        onClick={onDownloadBundle}
        disabled={downloadingBundle}
        variant="default"
        size="sm"
        className="whitespace-nowrap w-full"
      >
        {SafeComponentRenderer(downloadIcon, { className: "h-4 w-4 mr-2" }, <span aria-hidden="true" className="h-4 w-4 mr-2">⬇️</span>, "DownloadIcon")}
        {downloadingBundle ? "Downloading..." : "Download Support Snapshot"}
      </Button>
    </div>
  );
}

function SupportSnapshotConfigSummary({
  environment,
  authProviderType,
  database,
  storage
}: {
  environment: string,
  authProviderType: string | null,
  database: boolean,
  storage: boolean
}) {
  const getAuthProviderLabel = (type: string | null) => {
    switch (type) {
      case null:
      case '':
        return 'Not configured';
      case 'custom':
        return 'Custom';
      case 'manus':
        return 'Manus';
      default:
        return type ? `Unknown (${type})` : 'Not configured';
    }
  };

  return (
    <div className="text-xs bg-muted p-3 rounded-md">
      <div className="font-medium mb-1">Key Configuration</div>
      <div className="text-muted-foreground space-y-1">
        <div>• Environment: {environment || 'unknown'}</div>
        <div>• Auth Provider: {getAuthProviderLabel(authProviderType)}</div>
        <div>• Database: {database ? '✓ Connected' : '✗ Disconnected'}</div>
        <div>• Storage: {storage ? '✓ Connected' : '✗ Disconnected'}</div>
      </div>
    </div>
  );
}

function SupportSnapshotSystemInfo({
  appData
}: {
  appData: SupportSnapshotPanelProps['snapshotData']
}) {
  if (!appData) return null;

  return (
    <div className="grid grid-cols-2 gap-4 text-sm">
      <div>
        <div className="font-medium text-muted-foreground">Version</div>
        <div>{appData.app.version || 'unknown'}</div>
      </div>
      <div>
        <div className="font-medium text-muted-foreground">Node.js</div>
        <div>{appData.app.node}</div>
      </div>
      <div>
        <div className="font-medium text-muted-foreground">Platform</div>
        <div>{appData.app.platform}</div>
      </div>
      <div>
        <div className="font-medium text-muted-foreground">Uptime</div>
        <div>{Math.floor(appData.app.uptimeSec / 3600)}h {Math.floor((appData.app.uptimeSec % 3600) / 60)}m</div>
      </div>
    </div>
  );
}

function SupportSnapshotFeatureFlags({
  appData
}: {
  appData: SupportSnapshotPanelProps['snapshotData']
}) {
  if (!appData) return null;

  return (
    <div>
      <div className="font-medium mb-2">Feature Status</div>
      <div className="flex flex-wrap gap-2">
        <Badge variant={appData.storage.ok ? "default" : "secondary"}>
          {appData.storage.ok ? "✓" : "✗"} Storage Connected
        </Badge>
        <Badge variant={appData.features.offlineEnabled ? "default" : "secondary"}>
          {appData.features.offlineEnabled ? "✓" : "✗"} Offline Mode
        </Badge>
        <Badge variant={appData.features.authProvider ? "default" : "secondary"}>
          {appData.features.authProvider ? "✓" : "✗"} Auth Provider
        </Badge>
        <Badge variant={appData.db.ok ? "default" : "secondary"}>
          {appData.db.ok ? "✓" : "✗"} Database Connected
        </Badge>
      </div>
    </div>
  );
}

export function SupportSnapshotPanel({ snapshotData, fullSnapshot }: SupportSnapshotPanelProps) {
  // Safe debug check - only works in browser environment
  const debug = new URLSearchParams(window.location.search).get("debug") === "1";

  const [copyingSchematic, setCopyingSchematic] = useState(false);
  const [copyingContext, setCopyingContext] = useState(false);
  const [downloadingBundle, setDownloadingBundle] = useState(false);

  // Resolve icons safely - handles various bundler behaviors (.default vs direct export)
  const AlertCircleIcon = resolveComponent(AlertCircle);
  const DownloadIcon = resolveComponent(Download);
  const ServerIcon = resolveComponent(Server);

  // CALL ALL HOOKS UNCONDITIONALLY - This fixes React #300 "Rendered fewer hooks than expected"
  // Previously the trpc hook was called conditionally when fullSnapshot existed, violating Rules of Hooks
  const { data: schematic, isLoading, error, refetch } = trpc.system.getAppSchematic.useQuery(undefined, {
    staleTime: 30000, // Refetch every 30 seconds
    // Pass enabled: !!fullSnapshot to make hook behave correctly when not needed
    enabled: !!fullSnapshot,
  });

  // Wrap ALL RENDER LOGIC in try/catch (after all hooks) to catch any React #300 that escapes section boundaries
  try {
    // LOG COMPONENTS BEFORE ANY JSX - even if render crashes
    if (debug) {
      console.log('[SupportSnapshot] ENTER render build-marker: 2026-01-03-C');

      // Comprehensive component registry - every JSX element used in this file
      const componentRegistry = {
        // Shadcn UI components
        Card, CardContent, CardDescription, CardHeader, CardTitle,
        Badge, Button,
        // Resolved Lucide icons (should be functions)
        AlertCircleIcon, DownloadIcon, ServerIcon,
        // Custom components
        SectionBoundary
      };

      // Detailed preflight - log all types and invalid elements
      const preflightResults: string[] = [];
      Object.entries(componentRegistry).forEach(([name, component]) => {
        const typeOf = typeof component;
        if (typeOf !== 'function' && typeOf !== 'string') {
          preflightResults.push(`${name}: ${typeOf} (raw: ${component})`);
        }
      });

      console.log('[SupportSnapshot] ICON RESOLUTION: AlertCircle:', typeof AlertCircle, '->', typeof AlertCircleIcon, '| Download:', typeof Download, '->', typeof DownloadIcon, '| Server:', typeof Server, '->', typeof ServerIcon);
      console.log('[SupportSnapshot] PREF LIGHT RESULTS:', preflightResults.length > 0 ? preflightResults : 'All components valid');

      // Additional debug info
      console.log('[SupportSnapshot Debug] render props:', {
        snapshotData: typeof snapshotData,
        hasSnapshotData: !!snapshotData,
        fullSnapshot: typeof fullSnapshot,
        hasFullSnapshot: !!fullSnapshot,
      });
    }

    // Safety guard: check that all required components are defined
    if (!Card || !CardContent || !CardDescription || !CardHeader || !CardTitle ||
        !Badge || !Button || typeof AlertCircle === 'undefined' || typeof Download === 'undefined' || typeof Server === 'undefined') {
      console.error('[SupportSnapshot] Missing component imports:', {
        Card: !!Card, CardContent: !!CardContent, CardDescription: !!CardDescription,
        CardHeader: !!CardHeader, CardTitle: !!CardTitle, Badge: !!Badge, Button: !!Button,
        AlertCircle: !!AlertCircle, Download: !!Download, Server: !!Server
      });
      return (
        <div className="p-4 border border-destructive rounded-lg bg-destructive/10">
          <h3 className="font-medium text-destructive">Support Snapshot unavailable (component missing)</h3>
          <p className="text-sm text-muted-foreground">Check browser console for details. One or more required UI components could not be loaded.</p>
        </div>
      );
    }

    const buildTroubleshootingContext = (schematic: any, snapshotData: any): string => {
      if (!schematic || !snapshotData) return '';

      const { app, db, storage, features } = snapshotData;
      const now = new Date().toISOString();
      const uptimeHours = Math.floor(app.uptimeSec / 3600);
      const uptimeMinutes = Math.floor((app.uptimeSec % 3600) / 60);

      const nextChecks = [];
      if (!schematic.configFlags?.alertsConfigured) {
        nextChecks.push('- **Alerts not configured**: Set up ALERT_WEBHOOK_URL or SMTP for system notifications');
      }
      if (!schematic.configFlags?.backupsConfigured) {
        nextChecks.push('- **Backups not configured**: Run backup script: `scripts/b2-sync-uploads.sh`');
      }
      if (!schematic.configFlags?.smtpConfigured) {
        nextChecks.push('- **Email alerts disabled**: Configure ALERT_EMAIL_TO for email notifications');
      }
      if (!features.offlineEnabled) {
        nextChecks.push('- **Offline mode disabled**: Configure IndexedDB usage (system setting)');
      }

      return `# Water Well Logging - Troubleshooting Context
Generated: ${now}

## System Information
- **Version:** ${app.version}
- **Environment:** ${app.environment}
- **Platform:** ${app.platform}
- **Node.js Version:** ${app.node}
- **Uptime:** ${uptimeHours}h ${uptimeMinutes}m

## System Status
- **Database:** ${db.ok ? '✓ Connected' : '✗ Disconnected'}
- **Storage:** ${storage.ok ? '✓ Connected' : '✗ Disconnected'}
- **Auth Provider:** ${schematic.configFlags?.authProvider || 'unknown'}
- **Offline Mode:** ${features.offlineEnabled ? '✓ Enabled' : '✗ Disabled'}

## Feature Flags
- **Alerts Configured:** ${schematic.configFlags?.alertsConfigured ? '✓' : '✗'}
- **Backups Configured:** ${schematic.configFlags?.backupsConfigured ? '✓' : '✗'}
- **SMTP Configured:** ${schematic.configFlags?.smtpConfigured ? '✓' : '✗'}
- **Offline Enabled:** ${features.offlineEnabled ? '✓' : '✗'}

${nextChecks.length > 0 ? `## Next Checks
${nextChecks.join('\n')}
` : ''}## Endpoints Available
- System endpoints: ${schematic.endpointsMap?.system?.length || 0}
- Wells endpoints: ${schematic.endpointsMap?.wells?.length || 0}
- Daily logs endpoints: ${schematic.endpointsMap?.dailyLogs?.length || 0}

## App Schematic (JSON)
\`\`\`json
${JSON.stringify(schematic, null, 2)}
\`\`\`
`;
    };

    const handleCopySchematic = async () => {
      if (!schematic) return;

      try {
        setCopyingSchematic(true);
        await navigator.clipboard.writeText(JSON.stringify(schematic, null, 2));
        toast.success("App Schematic copied to clipboard", {
          description: "Ready for troubleshooting"
        });
      } catch (error: any) {
        toast.error("Failed to copy schematic", {
          description: error.message
        });
      } finally {
        setCopyingSchematic(false);
      }
    };

    const handleCopyContext = async () => {
      if (!snapshotData || !schematic) return;

      try {
        setCopyingContext(true);

        const markdown = buildTroubleshootingContext(schematic, snapshotData);
        await navigator.clipboard.writeText(markdown);
        toast.success("Troubleshooting context copied", {
          description: "Markdown summary for chat support"
        });
      } catch (error: any) {
        toast.error("Failed to copy context", {
          description: error.message
        });
      } finally {
        setCopyingContext(false);
      }
    };

    const handleDownloadBundle = async () => {
      try {
        setDownloadingBundle(true);

        const response = await fetch('/api/admin/support-bundle', {
          method: 'GET',
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        // Get filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'water-support-bundle.zip';
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="([^"]+)"/);
          if (match) {
            filename = match[1];
          }
        }

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success("Support bundle downloaded", {
          description: filename
        });
      } catch (error: any) {
        toast.error("Download failed", {
          description: error.message
        });
      } finally {
        setDownloadingBundle(false);
      }
    };

    if (!snapshotData) {
      return (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              {SafeComponentRenderer(AlertCircleIcon, { className: "h-5 w-5 text-destructive" }, <span aria-hidden="true" className="h-5 w-5 text-destructive">⚠️</span>, "AlertCircle")}
              <CardTitle>Support Snapshot</CardTitle>
            </div>
            <CardDescription>System information unavailable</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                System snapshot data is not available.
              </p>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <SectionBoundary name="Panel Header">
          <CardHeader>
            {/* Row 1: Title and description */}
            <SupportSnapshotHeader title="Support Snapshot" icon={ServerIcon} />
            <CardDescription>
              System schematic for troubleshooting and diagnostics
            </CardDescription>
            {fullSnapshot?.generatedAtISO && (
              <div className="text-xs text-muted-foreground">
                Generated: {new Date(fullSnapshot.generatedAtISO).toLocaleString()}
              </div>
            )}

            {/* Row 2: Actions bar */}
            <div className="mt-4 p-3 bg-muted rounded-md border">
              <SupportSnapshotActionsGrid
                onCopySchematic={handleCopySchematic}
                copyingSchematic={copyingSchematic}
                onCopyContext={handleCopyContext}
                copyingContext={copyingContext}
                onDownloadBundle={handleDownloadBundle}
                downloadingBundle={downloadingBundle}
                downloadIcon={DownloadIcon}
                serverIcon={ServerIcon}
              />
            </div>
          </CardHeader>
        </SectionBoundary>
        <CardContent className="space-y-4">
          <SectionBoundary name="System Information">
            <SupportSnapshotSystemInfo appData={snapshotData} />
          </SectionBoundary>

          <SectionBoundary name="Feature Flags">
            <SupportSnapshotFeatureFlags appData={snapshotData} />
          </SectionBoundary>

          <SectionBoundary name="Key Config Summary">
            <SupportSnapshotConfigSummary
              environment={snapshotData.app.environment}
              authProviderType={(schematic?.configFlags || fullSnapshot?.configFlags)?.authProvider || null}
              database={snapshotData.db.ok}
              storage={snapshotData.storage.ok}
            />
          </SectionBoundary>
        </CardContent>

        <div className="px-6 pb-6">
          <div className="text-xs bg-muted p-3 rounded-md">
            <div className="font-medium mb-1">What gets copied/downloaded:</div>
            <div className="text-muted-foreground space-y-1">
              <div>• <strong>Copy App Schematic:</strong> Complete system configuration JSON</div>
              <div>• <strong>Copy Troubleshooting Context:</strong> Human-readable summary and next steps for support</div>
              <div>• <strong>Download Support Snapshot:</strong> ZIP bundle with logs, health data, and full diagnostics</div>
            </div>
          </div>
        </div>
      </Card>
    );

  } catch (err: any) {
    // Fatal crash - catch any React #300 that escaped section boundaries
    console.error("[SupportSnapshot] RENDER CRASH", err);
    console.error("[SupportSnapshot] STACK", err?.stack);

    if (debug) {
      // In debug mode, show the actual error for diagnosis
      return (
        <div className="p-4 border-2 border-destructive rounded-lg bg-destructive/10 max-w-2xl">
          <h3 className="text-lg font-bold text-destructive mb-2">⚠️ Support Snapshot Render Error (Debug Mode)</h3>
          <div className="space-y-2">
            <div>
              <strong>Error:</strong> {String(err)}
            </div>
            <div>
              <strong>Stack:</strong>
              <pre className="mt-1 p-2 bg-destructive/20 rounded text-xs overflow-auto max-h-32">
                {err?.stack || 'No stack trace available'}
              </pre>
            </div>
          </div>
        </div>
      );
    } else {
      // In production, show safe fallback that doesn't crash the page
      return (
        <div className="p-3 border border-muted rounded-lg bg-muted/10">
          <div className="flex items-center gap-2 text-muted-foreground">
            <span aria-hidden="true">📊</span>
            <span className="text-sm">System diagnostics temporarily unavailable</span>
          </div>
        </div>
      );
    }
  }
}
